<template>
	<div>
		<vueDropzone 
			v-if="dropzone_loaded"
			ref="dropzone" 
			id="dropzone" 
			:options="dropzoneOptions"
			@vdropzone-success-multiple="onSuccess"
			@vdropzone-error="onError"
			@vdropzone-file-added="onAdded"
			:useCustomSlot=true
			class="mt-2"
		>
			<h5>{{ $t('invoice.supplier_dropzone') }} <font-awesome-icon :icon="['fal', 'download']"/></h5>
			<CustomTable
				id_table="invoice_suppliers_ocr_control"
				ref="invoice_suppliers_ocr_control"
				:items="invoices"
				:groupByCustom="customGroupBy"
				:busy.sync="table_busy"
				primaryKey="suppliersocr_id"
				:hide_if_empty="true"
				:hrefsRoutes="config_table_hrefs"
				:externSlotColumns="extern_slot_columns"
			>
				<template v-slot:[`custom-slot-cell(suppliersocr_num)`]="{ data }">
					<i v-if="data.suppliersocr_ocr_status == 'waiting'">{{ $t('invoice.supplier_waiting') }} <a href="#" @click.prevent="quickPreview(data.suppliersocr_pdf_url)"><font-awesome-icon :icon="['fas', 'eye']" /></a></i>
					<i v-else-if="data.suppliersocr_ocr_status == 'ocr_error'" class="text-danger">{{ $t('invoice.supplier_ocr_error') }} <a href="#" @click.prevent="quickPreview(data.suppliersocr_pdf_url)"><font-awesome-icon :icon="['fas', 'eye']" /></a></i>
					<template v-else>
						<router-link :to="{ name: 'invoiceSupplierFicheOverview', params: { invoicesupplier_id: data.suppliersocr_id } }">
							{{ data.suppliersocr_num }}
						</router-link>
						<a href="#" class="ml-1" @click.prevent="quickPreview(data.suppliersocr_pdf_url)"><font-awesome-icon :icon="['fas', 'eye']" /></a>
					</template>
				</template>
				<template v-slot:[`custom-slot-cell(suppliersocr_has_duplicate)`]="{ data }">
					<font-awesome-icon v-if="data.suppliersocr_has_duplicate" role="button" class="text-danger" :icon="['fas', 'external-link']" @click="openDuplicate(data.suppliersocr_id)"/>
					<font-awesome-icon class="text-success" v-else :icon="['fal', 'times-circle']" />
				</template>
				<template v-slot:[`custom-slot-cell(paymentmethod_code)`]="{ data }">
					<span v-if="data.paymentmethod_code">{{ $t('payment.payment_method_labels.' + data.paymentmethod_code.toLowerCase()) }}</span>
				</template>
			</CustomTable>
		</vueDropzone>

        <div v-if="upload_mode" class="col-12 mt-3 text-center">
            <button @click="save" class="btn btn-primary">{{ $t('global.ajouter') }} <font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin /></button>
        </div>

        <b-modal size="xl" ref="modelPreview" hide-footer @hidden="pdf_url = null">
            <template v-slot:modal-title>
                {{ $t("action.previsualiser") }}
            </template>

            <iframe v-if="pdf_url != null" :src="pdf_url" height="1000px" width="100%" style="position:relative"></iframe>
            <img v-else-if="img_url != null" :src="img_url" class="w-100"/>
            <div v-else>
                <LoadingSpinner />
            </div>
        </b-modal>
	</div>
</template>

<script type="text/javascript">
	import { EventBus } from "EventBus"
    import Tiers from "@/mixins/Tiers.js"
	import Invoice from "@/mixins/Invoice.js"
    import Navigation from "@/mixins/Navigation.js"
	import Shutter from "@/mixins/Shutter.js"
	import ShutterInvoice from "@/mixins/shutters-manager/Invoice.js"
    import Config from "@/mixins/Config.js"
	import Constants from 'Constants'
	import vue2Dropzone from 'vue2-dropzone'
	import 'vue2-dropzone/dist/vue2Dropzone.min.css'

	export default {
		name: 'ControlList',
		mixins: [Tiers, Invoice, Navigation, Shutter, ShutterInvoice, Config],
		props: ['tiers_id'],
		data () {
			return {
				invoices: [],
				table_busy: false,
                events_tab: {
                    'TableAction::goToAddInvoiceSupplier': () => { 
						this.openAddInvoiceSupplier(this.tiers_id)
                    },
                    'TableAction::goToControlInvoiceSupplier': this.goToControlInvoiceSupplier,
                    'TableAction::goToDeleteInvoiceSupplier': this.goToDeleteInvoiceSupplier,
                    'TableAction::goToPrintInvoiceSupplier': this.printInvoiceSupplier,
                },
				extern_slot_columns: ['suppliersocr_num', 'suppliersocr_has_duplicate', 'paymentmethod_code'],
				customGroupBy: {'paymentmethod_code': this.getTradPaymentMethod},
                config_table_hrefs: {
                    'entity.tiers_rs': {
                        routeUniqueName: 'entityTableauBord',
                        routeName: 'tiersTableauBord',
                        params: {
                            tiers_id: 'entity.tiers_id'
                        }
                    },
                    'tier.tiers_rs': {
                        routeUniqueName: 'tiersTableauBord',
                        routeName: 'tiersTableauBord',
                        params: {
                            tiers_id: 'tier.tiers_id'
                        }
                    },
                },
                pdf_url: null,
                img_url: null,
                upload_mode: false,
                processing: false,
				dropzone_loaded: false,
				dropzoneOptions: {
					thumbnailWidth: 250,
					acceptedFiles: 'image/*,application/pdf',
					autoProcessQueue: false,
					uploadMultiple: true,
					clickable: false,
					dictDefaultMessage: this.getTrad('invoice.upload_invoice_supplier'),
					headers: {
						"Authorization" : "Bearer " + this.getConfig('token'),
						withCredentials: true
					}
				},
				file_uploaded: 0,
				// Attente de la fin d'une lecture OCR, armée seulement quand une facture en attend une.
				ocr_timer: null,
				ocr_delay: 3000,
				ocr_watch_started_at: null
			}
		},

		mounted() {
			this.init_component()

			// Le filtre de l'en-tête vaut pour tous les onglets : la liste se recharge quand il change.
			EventBus.$on('InvoiceSupplier::EntitiesChanged', this.loadInvoicesSupplier)
			EventBus.$on('InvoiceSupplier::Refresh', this.loadInvoicesSupplier)
		},

		beforeDestroy() {
			EventBus.$off('InvoiceSupplier::EntitiesChanged', this.loadInvoicesSupplier)
			EventBus.$off('InvoiceSupplier::Refresh', this.loadInvoicesSupplier)
			clearTimeout(this.ocr_timer)
		},

		methods: {
			init_component() {
				this.loadInvoicesSupplier()


				this.dropzoneOptions.url = this.getUrlInvoiceSupplierTiers(this.tiers_id)
				if(this.tiers_id) {
					this.dropzoneOptions.url = this.getUrlInvoiceSupplierTiers(this.tiers_id)
				}
				else {
					this.dropzoneOptions.url = Constants.INVOICES_SUPPLIERS_OCR_URL + "?licence_key="+Constants.USER_LICENCE_KEY
				}
				this.dropzone_loaded = true
			},

			async loadInvoicesSupplier() {
				if(this.$refs.invoice_suppliers_ocr_control) {
					this.$refs.invoice_suppliers_ocr_control.unselectAll()
				}

				// Le filtre de comptabilité est tenu par l'en-tête de la page, pour tous les onglets.
				let entity_ids = this.getConfig('invoice_suppliers_entities') || []
				// dans la fiche tiers je ne filtre par sur les entités donc je passe le tableau à vide
				if(this.tiers_id) {
					entity_ids = []
				}

				this.table_busy = true
				if(this.tiers_id) {
					this.invoices = await this.getInvoiceSupplierControlByTiers(this.tiers_id, entity_ids)
				}
				else {
					this.invoices = await this.getInvoiceSupplierControl(entity_ids)
				}
				this.table_busy = false

				this.watchPendingOcr()
			},

			/**
			 * Une facture déposée est lue par l'OCR en tâche de fond : ses montants, son numéro et son
			 * fournisseur n'existent pas encore quand la ligne apparaît. Tant qu'une lecture est en
			 * cours, la liste se redemande d'elle-même ; dès qu'il n'en reste aucune, elle s'arrête.
			 *
			 * La première relecture vient vite — une lecture aboutit d'ordinaire en quelques secondes —
			 * puis l'attente s'allonge : une lecture qui s'éternise n'a pas à être guettée au même rythme.
			 *
			 * Le guet s'arrête au bout de trente secondes. Une lecture qui n'a pas abouti d'ici là ne le
			 * fera pas dans les suivantes : le service de lecture est indisponible, ou nous refuse le
			 * service. Continuer à l'interroger n'y changerait rien et ajouterait à sa charge, alors que
			 * l'écran restera peut-être ouvert des heures. Le statut se verra au prochain affichage.
			 */
			watchPendingOcr() {
				clearTimeout(this.ocr_timer)

				if(!this.invoices.some(invoice => invoice.suppliersocr_ocr_status == 'waiting')) {
					this.ocr_delay = 3000
					this.ocr_watch_started_at = null
					return
				}

				if(this.ocr_watch_started_at === null) {
					this.ocr_watch_started_at = Date.now()
				}

				// Le prochain rechargement est armé s'il tombe encore dans la fenêtre : c'est le départ de
				// la requête qui compte, pas la décision de la programmer.
				const elapsed = Date.now() - this.ocr_watch_started_at

				if(elapsed + this.ocr_delay > 30000) {
					return
				}

				this.ocr_timer = setTimeout(this.loadInvoicesSupplier, this.ocr_delay)
				this.ocr_delay = Math.min(this.ocr_delay * 1.5, 15000)
			},

			/**
			 * Ouvre la facture sur son onglet Contrôle plutôt que le formulaire seul : la fiche donne
			 * accès au même écran, mais aussi au détail des lignes, au cycle de vie et aux règlements,
			 * qu'on consulte souvent pour trancher un contrôle.
			 */
			goToControlInvoiceSupplier(invoice_supplier) {
				this.$router.push({
					name: 'invoiceSupplierFicheControl',
					params: { invoicesupplier_id: invoice_supplier.suppliersocr_id }
				})
			},

			async goToDeleteInvoiceSupplier(invoicesupplier_ids) {
				await this.deleteInvoiceSupplier(invoicesupplier_ids)
				EventBus.$emit("TableAction::stopSpin")
				this.successToast('toast.info_save_succes')
				this.refresh()
			},	

            async quickPreview(pdf_url) {
                this.$refs.modelPreview.show()
                this.pdf_url = null
                this.img_url = null
                if(pdf_url.includes('.pdf'))
					this.pdf_url = pdf_url
				else
					this.img_url = pdf_url
            },

            async openDuplicate(id) {
				let duplicate = await this.getDuplicationInvoiceSupplier(id)

				this.setUpControlDuplicate(duplicate)
            },

			async save() {
				if(this.processing) return false
				this.file_uploaded = 0
				this.processing = true
				this.$refs.dropzone.processQueue()
			},

			onSuccess(files) {
				this.file_uploaded += files.length
				if(this.$refs.dropzone.getAcceptedFiles().length != this.file_uploaded) {
					return false
				}

				this.processing = false
				this.successToast('toast.info_save_succes')
				this.refresh()
				this.dropzone_loaded = false
				this.$nextTick(() => {
					this.dropzone_loaded = true
				})
				this.upload_mode = false
			},

			onError() {
				this.processing = false
				this.failureToast("toast.info_save_failed")
			},

			onAdded() {
				this.upload_mode = true
				this.$nextTick(()=> {
					this.save()
				})
			},

			/**
			 * Après une action qui déplace des factures, la liste et les pastilles des onglets
			 * doivent dire la même chose.
			 */
			refresh() {
				this.loadInvoicesSupplier()
				EventBus.$emit('InvoiceSupplier::CountsChanged')
			}
		},

		components: {
            CustomTable: () => import('GroomyRoot/components/Table/CustomTable'),
			vueDropzone: vue2Dropzone
		}
	}

</script>